<template>
<div class="page-container quick-quote-page">
  <section class="quick-quote-card">
    <div class="quick-quote-card-heading">
      <div class="quick-quote-card-title">{{ $t('查询条件') }}</div>
      <div class="quick-quote-actions">
        <el-button size="small" type="primary" icon="el-icon-search" :loading="quoteLoading" @click="search">{{ $t('查询预估价格') }}</el-button>
        <el-button size="small" icon="el-icon-refresh-left" @click="reset">{{ $t('重置') }}</el-button>
        <span>{{ $t('按单件、1 件试算；结果包括当前产品加价。') }}</span>
      </div>
    </div>
    <div class="quick-quote-fields">
      <label class="quick-quote-field quick-quote-zip">
        <span>{{ $t('收件邮编') }} <em>*</em></span>
        <el-input v-model="form.zip" maxlength="5" inputmode="numeric" clearable :placeholder="$t('美国 5 位邮编')" @input="onZipInput" @keyup.enter.native="search" />
        <small v-if="location" class="quick-quote-location">{{ location.state }} / {{ location.city }}</small>
        <small v-else-if="locationLoading" class="quick-quote-hint"><i class="el-icon-loading" />{{ $t(' 正在识别邮编') }}</small>
        <small v-else-if="locationError" class="quick-quote-error">{{ $t(locationError) }}</small>
      </label>
      <label class="quick-quote-field">
        <span>{{ $t('发货地') }}</span>
        <el-select v-model="form.shippingArea" clearable :placeholder="$t('全部发货地')" @change="clearResults">
          <el-option v-for="region in regions" :key="region.id" :label="region.label" :value="region.id" />
        </el-select>
      </label>
      <label v-for="field in measurementFields" :key="field.key" class="quick-quote-field">
        <span>{{ field.label }} <em>*</em></span>
        <el-input v-model.trim="form[field.key]" inputmode="decimal" :placeholder="field.placeholder" @input="clearResults" @keyup.enter.native="search">
          <span slot="suffix" class="quick-quote-unit">{{ field.unit }}</span>
        </el-input>
      </label>
    </div>
  </section>

  <section class="quick-quote-results">
    <div class="quick-quote-results-head">
      <div>
        <h2>{{ $t('产品报价') }}</h2>
        <p v-if="quotes.length">{{ $t(`已返回 ${completedCount} / ${visibleQuotes.length} 个产品`) }}</p>
        <p v-else>{{ $t('查询后显示所选发货地产品的预估价格') }}</p>
      </div>
      <el-tag v-if="quoteLoading" type="info" effect="plain"><i class="el-icon-loading" /> {{ $t('正在向平台试算') }}</el-tag>
    </div>
    <el-alert v-if="visibleQuotes.length" :title="$t('预估价仅供参考：上游接口要求完整收件信息，本查询使用邮编对应的州/城市和估价占位地址。住宅、偏远及其他附加费可能因实际地址改变；最终金额以创建订单时的完整试算为准。')" type="warning" :closable="false" show-icon />
    <el-table v-if="visibleQuotes.length" :data="pagedQuotes" border stripe class="quick-quote-table" :empty-text="$t('暂无报价')">
      <el-table-column :label="$t('发货区域')" width="110"><template slot-scope="scope">{{ $t(scope.row.area) }}</template></el-table-column>
      <el-table-column prop="productName" :label="$t('产品名称')" min-width="210" show-overflow-tooltip />
      <el-table-column prop="productCode" :label="$t('产品编码')" min-width="150" show-overflow-tooltip />
      <el-table-column :label="$t('计费重')" width="130"><template slot-scope="scope">{{ scope.row.chargeableWeight == null ? '—' : `${scope.row.chargeableWeight} ${scope.row.weightUnit || ''}` }}</template></el-table-column>
      <el-table-column :label="$t('预估价格')" min-width="285"><template slot-scope="scope"><quote-price :quote="scope.row" @retry="retryQuote" /></template></el-table-column>
    </el-table>
    <div v-else class="quick-quote-empty">
      <i class="el-icon-s-data" />
      <strong>{{ quotes.length ? $t('暂无可展示的报价') : $t('输入包裹信息后查询价格') }}</strong>
      <span>{{ quotes.length ? $t('所选产品暂未返回可用价格，请调整包裹信息后重试。') : $t('不会创建订单，也不会扣除账户余额。') }}</span>
    </div>
    <pagination v-if="visibleQuotes.length" :total="visibleQuotes.length" :page.sync="page" :limit.sync="pageSize" :auto-scroll="false" />
  </section>
</div>
</template>

<script>
import { translateUi } from '../../i18n'
import { mapGetters } from 'vuex'
import Pagination from '@/components/Pagination'
import { listProducts, quickQuote } from '@/api/transport'
import QuotePrice from './QuotePrice.vue'
import { legacyProductName, legacyProductCategory } from './orderOptions'
import { lookupUsZip } from './usZipLookup'
import { isVisibleQuote } from './quoteVisibility'

const regions = [
  { id: '3', get label () { return translateUi('美东') } },
  { id: '2', get label () { return translateUi('美中') } },
  { id: '1', get label () { return translateUi('美西') } }
]

export default {
  name: 'TransportQuickQuote',
  components: { Pagination, QuotePrice },
  data () {
    return {
      form: { zip: '', shippingArea: '', weight: '', length: '', width: '', height: '' },
      measurementFields: [
        { key: 'weight', get label () { return translateUi('重量') }, unit: 'kg', get placeholder () { return translateUi('单件重量') } },
        { key: 'length', get label () { return translateUi('长') }, unit: 'cm', get placeholder () { return translateUi('长度') } },
        { key: 'width', get label () { return translateUi('宽') }, unit: 'cm', get placeholder () { return translateUi('宽度') } },
        { key: 'height', get label () { return translateUi('高') }, unit: 'cm', get placeholder () { return translateUi('高度') } }
      ],
      location: null,
      locationLoading: false,
      locationError: '',
      zipRequestId: 0,
      regions,
      products: [],
      productsLoading: false,
      quotes: [],
      quoteLoading: false,
      quoteRevision: 0,
      page: 1,
      pageSize: 20
    }
  },
  computed: {
    ...mapGetters(['roles']),
    isAdmin () { return this.roles === 'ROLE_ADMIN' },
    visibleQuotes () { return this.quotes.filter(isVisibleQuote) },
    selectedProducts () {
      return this.form.shippingArea ? this.products.filter(product => product.areaId === this.form.shippingArea) : this.products
    },
    pagedQuotes () { return this.visibleQuotes.slice((this.page - 1) * this.pageSize, this.page * this.pageSize) },
    completedCount () { return this.visibleQuotes.filter(row => row.status !== 'PENDING').length }
  },
  created () { this.loadProducts() },
  beforeDestroy () { this.quoteRevision++; this.zipRequestId++ },
  methods: {
    async loadProducts () {
      this.productsLoading = true
      try {
        const available = await listProducts(this.isAdmin)
        const seen = new Set()
        this.products = regions.flatMap(region => (available || []).reduce((rows, product) => {
          if (product.enabled && String(product.shippingArea) === region.id && !seen.has(product.code)) {
            seen.add(product.code)
            rows.push({ code: product.code, name: legacyProductName(product), category: legacyProductCategory(product), area: region.label, areaId: region.id })
          }
          return rows
        }, []))
      } catch (error) {
        this.$message.error(translateUi((error && error.message) || translateUi('产品读取失败，请刷新页面重试')))
      } finally {
        this.productsLoading = false
      }
    },
    clearResults () {
      this.quoteRevision++
      this.quoteLoading = false
      this.quotes = []
      this.page = 1
    },
    onZipInput (value) {
      const zip = String(value || '').replace(/\D/g, '').slice(0, 5)
      if (zip !== this.form.zip) this.form.zip = zip
      this.clearResults()
      this.zipRequestId++
      this.location = null
      this.locationError = ''
      this.locationLoading = false
      if (!/^\d{5}$/.test(zip)) return
      const requestId = this.zipRequestId
      this.locationLoading = true
      return lookupUsZip(zip).then(location => {
        if (requestId !== this.zipRequestId) return
        if (location) this.location = { state: location.state, city: location.primaryCity }
        else this.locationError = '此邮编暂无上游城市记录，请核对邮编或联系客服'
      }).catch(() => {
        if (requestId === this.zipRequestId) this.locationError = '邮编城市读取失败，请重试或联系客服'
      }).finally(() => { if (requestId === this.zipRequestId) this.locationLoading = false })
    },
    validate () {
      if (!/^\d{5}$/.test(this.form.zip)) return translateUi('请输入有效的美国 5 位邮编')
      if (!this.location || !this.location.state || !this.location.city) return translateUi('请等待邮编识别完成后再查询')
      for (const field of this.measurementFields) {
        const value = String(this.form[field.key] || '').trim()
        const decimals = field.key === 'weight' ? 3 : 1
        if (!new RegExp(`^\\d{1,3}(?:\\.\\d{1,${decimals}})?$`).test(value) || Number(value) <= 0 || Number(value) > 999) {
          return translateUi(`${field.label}需大于 0 且不超过 999，最多 ${decimals} 位小数`)
        }
      }
      if (!this.selectedProducts.length) return this.productsLoading ? '正在加载产品，请稍后重试' : '当前发货地没有可试算的产品，请联系管理员'
      return ''
    },
    requestData (codes) {
      return {
        zip: this.form.zip,
        state: this.location.state,
        city: this.location.city,
        weight: this.form.weight,
        length: this.form.length,
        width: this.form.width,
        height: this.form.height,
        selectedProductCodes: codes
      }
    },
    replaceQuote (code, result) {
      const index = this.quotes.findIndex(row => row.productCode === code)
      if (index >= 0) {
        this.$set(this.quotes, index, {
          ...this.quotes[index],
          ...result,
          productCode: code,
          productName: legacyProductName({ code, name: result && result.productName }),
          category: legacyProductCategory({ code, category: result && result.category })
        })
      }
    },
    async search () {
      if (this.quoteLoading || this.quotes.some(row => row.status === 'PENDING')) return
      const error = this.validate()
      if (error) return this.$message.warning(translateUi(error))
      this.clearResults()
      const revision = this.quoteRevision
      const products = this.selectedProducts
      this.quotes = products.map(product => ({ productCode: product.code, productName: legacyProductName(product), category: legacyProductCategory(product), area: product.area, status: 'PENDING' }))
      this.quoteLoading = true
      const batches = []
      for (let index = 0; index < products.length; index += 4) batches.push(products.slice(index, index + 4).map(item => item.code))
      let nextBatch = 0
      const worker = async () => {
        while (nextBatch < batches.length && revision === this.quoteRevision) {
          const codes = batches[nextBatch++]
          try {
            const result = await quickQuote(this.requestData(codes))
            if (revision !== this.quoteRevision) return
            if (!Array.isArray(result && result.quotes)) throw new Error('未收到试算结果')
            const returned = new Map(result.quotes.map(row => [row.productCode, row]))
            codes.forEach(code => this.replaceQuote(code, returned.get(code) || { status: 'FAILED', failureReason: '该产品未返回试算结果' }))
          } catch (error) {
            if (revision !== this.quoteRevision) return
            codes.forEach(code => this.replaceQuote(code, { status: 'FAILED', failureReason: (error && error.message) || '试算失败，请重试' }))
          }
        }
      }
      await Promise.all(Array.from({ length: Math.min(2, batches.length) }, () => worker()))
      if (revision === this.quoteRevision) this.quoteLoading = false
    },
    async retryQuote (row) {
      if (!row) return
      const current = this.quotes.find(item => item.productCode === row.productCode)
      if (!current || current.status !== 'FAILED') return
      const error = this.validate()
      if (error) return this.$message.warning(translateUi(error))
      const revision = this.quoteRevision
      this.replaceQuote(row.productCode, { status: 'PENDING', failureReason: '' })
      try {
        const result = await quickQuote(this.requestData([row.productCode]))
        if (revision !== this.quoteRevision) return
        const updated = Array.isArray(result && result.quotes) ? result.quotes.find(item => item.productCode === row.productCode) : null
        this.replaceQuote(row.productCode, updated || { status: 'FAILED', failureReason: '该产品未返回试算结果' })
      } catch (error) {
        if (revision === this.quoteRevision) this.replaceQuote(row.productCode, { status: 'FAILED', failureReason: (error && error.message) || '试算失败，请重试' })
      }
    },
    reset () {
      this.zipRequestId++
      this.form = { zip: '', shippingArea: '', weight: '', length: '', width: '', height: '' }
      this.location = null
      this.locationError = ''
      this.locationLoading = false
      this.clearResults()
    }
  }
}
</script>

<style scoped>
.quick-quote-page { min-height: 100%; padding: 24px; background: #f5f7fa; color: #243542; box-sizing: border-box; }
.quick-quote-results-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; }
.quick-quote-results-head p { margin: 0; color: #71808b; font-size: 13px; line-height: 1.6; }
.quick-quote-card, .quick-quote-results { background: #fff; border: 1px solid #e7ecf0; border-radius: 10px; box-shadow: 0 2px 14px rgba(27, 51, 73, .035); }
.quick-quote-card { padding: 14px 18px 12px; }
.quick-quote-card-heading { display: flex; align-items: center; justify-content: space-between; gap: 18px; margin-bottom: 12px; }
.quick-quote-card-title { flex: none; font-size: 15px; font-weight: 600; }
.quick-quote-fields { display: grid; grid-template-columns: repeat(6, minmax(125px, 1fr)); gap: 12px; }
.quick-quote-field { display: flex; flex-direction: column; gap: 5px; min-width: 0; font-size: 13px; color: #435562; }
.quick-quote-field em { color: #f05b5b; font-style: normal; }
.quick-quote-field small { font-size: 12px; line-height: 14px; min-height: 14px; }
.quick-quote-zip { position: relative; }
.quick-quote-location { color: #278466; }
.quick-quote-hint { color: #748591; }
.quick-quote-error { color: #e05656; }
.quick-quote-unit { color: #83939d; line-height: 40px; padding-right: 5px; }
.quick-quote-actions { display: flex; align-items: center; flex-wrap: wrap; justify-content: flex-end; gap: 8px; margin-top: 0; }
.quick-quote-actions > span { color: #8896a0; font-size: 12px; margin-left: 6px; }
.quick-quote-results { display: flex; flex-direction: column; min-height: 350px; margin-top: 20px; padding: 22px 24px 10px; }
.quick-quote-results-head { align-items: center; margin-bottom: 18px; }
.quick-quote-results-head h2 { font-size: 17px; margin: 0 0 4px; }
.quick-quote-results .el-alert { margin-bottom: 16px; }
.quick-quote-table { width: 100%; }
.quick-quote-empty { flex: 1; min-height: 230px; display: flex; flex-direction: column; justify-content: center; align-items: center; gap: 9px; color: #94a1aa; }
.quick-quote-empty i { font-size: 34px; color: #b7c6d0; }
.quick-quote-empty strong { color: #4b5b66; font-size: 14px; }
.quick-quote-empty span { font-size: 12px; }
.quick-quote-results .pagination-container { margin-top: auto; }
@media (max-width: 1100px) { .quick-quote-fields { grid-template-columns: repeat(3, minmax(150px, 1fr)); } }
@media (max-width: 700px) { .quick-quote-card-heading { align-items: flex-start; flex-direction: column; gap: 10px; } .quick-quote-actions { justify-content: flex-start; } }
@media (max-width: 520px) { .quick-quote-page { padding: 12px; } .quick-quote-card, .quick-quote-results { padding: 16px; } .quick-quote-fields { grid-template-columns: 1fr; } .quick-quote-results-head { flex-wrap: wrap; gap: 8px; } .quick-quote-actions { width: 100%; } .quick-quote-actions > span { width: 100%; margin-left: 0; } }
</style>
